"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FocusTrap } from "focus-trap-react";
import { cn } from "../utils/cn.js";
import { Icon } from "./Icon.js";
/**
 * Drawer — focus-trapped slide-in panel from right or left. Default width
 * 420px on md+; full-vw on mobile. ESC + backdrop close + scroll-lock.
 *
 * Renders into `document.body` via createPortal. SSR-safe.
 */
export function Drawer({ open, onClose, side = "right", title, children, footer, width = 420, closeOnBackdrop = true, closeOnEsc = true, className, }) {
    const overlayRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape" && closeOnEsc)
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [open, closeOnEsc, onClose]);
    if (!open || typeof document === "undefined")
        return null;
    const widthStyle = typeof width === "number" ? `${width}px` : width;
    const panelStyle = { "--wm-drawer-w": widthStyle };
    return createPortal(_jsx(FocusTrap, { focusTrapOptions: {
            escapeDeactivates: false,
            allowOutsideClick: true,
        }, children: _jsx("div", { ref: overlayRef, "data-slot": "drawer-overlay", role: "dialog", "aria-modal": "true", className: "fixed inset-0 z-50 flex bg-ink-900/30 backdrop-blur-sm", onMouseDown: (e) => {
                if (closeOnBackdrop && e.target === overlayRef.current)
                    onClose();
            }, style: { justifyContent: side === "right" ? "flex-end" : "flex-start" }, children: _jsxs("aside", { "data-slot": "drawer-panel", "data-side": side, className: cn("h-full w-full max-w-full overflow-hidden bg-ivory shadow-3 flex flex-col", "md:w-[var(--wm-drawer-w)]", className), style: panelStyle, children: [title ? (_jsxs("header", { "data-slot": "drawer-header", className: "flex items-center justify-between border-b border-ink-200 px-5 py-4", children: [_jsx("h2", { className: "font-serif text-title-md text-ink-900", children: title }), _jsx("button", { type: "button", "aria-label": "Close", onClick: onClose, className: "rounded-sm p-1 text-ink-500 hover:bg-accent-soft outline-none focus-visible:shadow-focus", children: _jsx(Icon, { name: "x", size: 18, "aria-hidden": true }) })] })) : null, _jsx("div", { "data-slot": "drawer-body", className: "flex-1 overflow-y-auto px-5 py-4", children: children }), footer ? (_jsx("footer", { "data-slot": "drawer-footer", className: "border-t border-ink-200 bg-paper-warm px-5 py-4", children: footer })) : null] }) }) }), document.body);
}
